/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useMemo } from "react";
import { sanitizeHtml } from "../../../utils/sanitize";
import { format } from "date-fns";
import styled from "styled-components";
import Stack from "../../ui/Stack";
import Icon from "../../ui/Icon";
import { useFirstAvailableDay } from "../../../lib/react-query/useFirstAvailableDay";
import Text from "../../ui/Text";
import TimeSlots from "../../TimeSlots";
import Title from "../../ui/Title";
import SkeletonText from "../../skeleton/SkeletonText";
import { useAssetsSrc } from "../../../hooks/useAssetsSrc";
import Button from "../../ui/Button";
import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalImage, ModalTitle, ModalTrigger } from "../../ui/Modal";
import BookingStepItem from "../steps/BookingStepItem";
import T from "../../ui/Translation";
import FormatDateTime from "../../formatters/FormatDateTime";
import { useBooking } from "../../../hooks/useBooking";
import { useTimeSlotSelection } from "../../../hooks/useTimeSlotSelection";
import { useTimeSlots } from "../../../lib/react-query/useTimeSlots";
import { useConfig } from "../../../lib/react-query/useConfig";
import { getTodayInTimezone } from "../../../utils/helpers/date";
import { useMembershipCoverage } from "../../../hooks/useMembershipCoverage";
import { useStepManager } from "../../../hooks/useStepManager";
import { ROUTES } from "../../../constants/constants";

const StyledProviderItem = styled((props) => <BookingStepItem {...props} />)`
  padding: 1rem;
`;

const ProviderItemInner = styled.div`
  position: relative;
  display: grid;
  place-content: start;
  align-items: center;
  grid-template-columns: auto 1fr;
  gap: 0 1rem;
`;

const ProviderItemMedia = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
  border: 1px solid rgba(${({ theme }) => theme.colors.iconStroke.rgba});
`;

const ProviderItemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;


const ProviderInfoButton = styled((props) => <Button {...props} />)`
  position: absolute;
  right: 0;
  top: 0;
`;

const ProviderNearestDateSlots = styled.div`
  display: grid;
  gap: 0.5rem;
  grid-column: 2 / span 2;

  ${({ theme }) => theme.containerMedia.sm} {
    grid-column: 1 / span 2;
    margin-top: 1rem;
  }
`;

const ProviderItem = ({ provider }) => {
  const {
    selectedProvider,
    selectedServices,
    selectedTime,
    setSelectedProvider,
    setSelectedTime,
    selectedLocation,
    setSelectedDate,
  } = useBooking();
  const { selectTimeSlot, pendingTime } = useTimeSlotSelection();
  const providerImage = useAssetsSrc(provider.picture_preview);
  const isSelected = provider.id === selectedProvider?.id;
  const isAvailable = provider.is_available;
  const isAnyProvider = provider.id === "any";
  const { navigateToStep } = useStepManager();

  const { data: config } = useConfig();
  const { availabilityParams, clampDay } = useMembershipCoverage(selectedServices);
  const productIds = selectedServices.flatMap((s) => [...(s.addons ?? []), ...(s.products ?? [])].map((p) => p.id));
  const { data: firstAvailableDay } = useFirstAvailableDay(
    {
      count: selectedServices.find((service) => service.group_booking_count)?.group_booking_count || 1,
      provider_id: provider.id,
      "service_id[]": selectedServices.map((service) => service.id),
      location_id: selectedLocation?.id,
      ...(productIds.length > 0 && { "products[]": productIds }),
      ...availabilityParams,
    },
    {
      enabled: !!selectedServices.length && !isAnyProvider,
    }
  );

  // Client's local date can be ahead of the company timezone date.
  const adjustedFirstAvailableDay = useMemo(() => {
    if (!firstAvailableDay || !config?.timezone) return firstAvailableDay;

    const companyToday = getTodayInTimezone(config.timezone);
    const clientToday = format(new Date(), "yyyy-MM-dd");

    if (firstAvailableDay === companyToday && clientToday > companyToday) {
      return clientToday;
    }

    return firstAvailableDay;
  }, [firstAvailableDay, config?.timezone]);

  const nearestBookableDay = clampDay(adjustedFirstAvailableDay);

  const { data: slots, isFetching: isFetchingSlots } = useTimeSlots(
    {
      count: selectedServices.find((service) => service.group_booking_count)?.group_booking_count || 1,
      date_from: nearestBookableDay,
      date_to: nearestBookableDay,
      provider_id: provider.id === "any" ? null : provider.id,
      "service_id[]": selectedServices.map((service) => service.id),
      location_id: selectedLocation?.id,
      ...(productIds.length > 0 && { "product_ids[]": productIds }),
    },
    {
      enabled: provider.id !== "any" && !!nearestBookableDay,
    }
  );

  const todayIsAvailable = useMemo(() => {
    if (!nearestBookableDay) return false;

    return format(new Date(), "yyyy-MM-dd") === nearestBookableDay;
  }, [nearestBookableDay]);

  const handleProviderSelect = () => {
    selectedProvider && selectedProvider.id !== provider.id && setSelectedTime(null);
    setSelectedProvider(provider);
  };

  const handleMoreSlotsClick = () => {
    setSelectedProvider(provider);
    setSelectedDate(nearestBookableDay);
    navigateToStep(ROUTES.DATETIME);
  };

  const handleSlotClick = (time) => {
    handleProviderSelect();
    selectTimeSlot(nearestBookableDay, time, provider);
  };

  return (
    <StyledProviderItem
      disabled={!isAvailable}
      selected={isSelected}
      onClick={isAvailable ? handleProviderSelect : undefined}
    >
      <ProviderItemInner>
        <ProviderItemMedia>
          {providerImage && <ProviderItemImage src={providerImage} alt={provider.name} />}
          {!providerImage && !isAnyProvider && <Icon.User size={Icon.SIZE.SM} color="iconStroke" />}
          {!providerImage && isAnyProvider && <Icon.Shuffle size={Icon.SIZE.SM} />}
        </ProviderItemMedia>

        <Stack $gap="1.5rem">
          <Title size="h3">{isAnyProvider && !provider.name ? "any_unit" : provider.name}</Title>
        </Stack>

        {(isAnyProvider || provider.description) && (
          <Modal>
            <ModalTrigger>
              <ProviderInfoButton
                variant="transparent"
                size="icon"
                icon={<Icon.InfoCircle color="secondary" />}
              />
            </ModalTrigger>
            <ModalContent>
              {providerImage ? (
                <>
                  <ModalCloseButton />
                  <ModalImage src={providerImage} alt={provider.name} />
                </>
              ) : (
                <ModalHeader>
                  <ModalTitle>
                    {isAnyProvider ? provider.name === "" ? <T>any_unit</T> : provider.name : provider.name}
                  </ModalTitle>
                </ModalHeader>
              )}
              <ModalBody>
                {providerImage && (
                  <ModalTitle>
                    {isAnyProvider ? provider.name === "" ? <T>any_unit</T> : provider.name : provider.name}
                  </ModalTitle>
                )}
                {isAnyProvider && provider.description.length === 0 && <T>any_unit_description</T>}
                {provider.description && <Text dangerouslySetInnerHTML={{ __html: sanitizeHtml(provider.description) }} />}
              </ModalBody>
            </ModalContent>
          </Modal>
        )}

        {!isAnyProvider && isAvailable && (isFetchingSlots || slots?.length > 0) && (
          <ProviderNearestDateSlots>
            {isFetchingSlots && <SkeletonText size="small" width="60%" />}
            {!isFetchingSlots && (
              <Text color="dark" size="small">
                {todayIsAvailable ? <T>Nearest available slots today</T> : <T>Nearest available slots on</T>}{" "}
                {!todayIsAvailable && (
                  <b>
                    <FormatDateTime value={nearestBookableDay} format={FormatDateTime.FORMAT.DATE} />
                  </b>
                )}
              </Text>
            )}

            <TimeSlots
              isLoading={isFetchingSlots}
              slots={slots}
              selectedTime={selectedProvider?.id === provider.id ? selectedTime : null}
              pendingTime={pendingTime}
              onSlotClick={handleSlotClick}
              maxSlots={3}
              onMoreSlotsClick={handleMoreSlotsClick}
            />
          </ProviderNearestDateSlots>
        )}
      </ProviderItemInner>
    </StyledProviderItem>
  );
};

export default ProviderItem;
